import { Box, Typography, IconButton, Breadcrumbs } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import "./Educationpagestyles.css";
import { DEFAULT_SOURCES } from "../../data/carecopilotSources";

/**
 * EducationShell — lightweight content wrapper only.
 *
 * Navigation between education content now lives entirely in the global
 * app nav (LEARN / TRIALS / TOOLS / LIBRARY dropdowns), so this shell no
 * longer owns any tabs, dropdowns, or a mobile bottom sheet. It just frames
 * the page: an optional breadcrumb trail, the content itself, and the
 * shared Sources & References footer.
 *
 * @param {Array<{label: string, path?: string}>} breadcrumbs
 *   e.g. [{ label: "Learn" }, { label: "Amyloidosis Types", path: "/education/types" }, { label: "AL" }]
 *   The last entry renders as plain (current page) text, not a link.
 */
export default function EducationShell({ children, breadcrumbs = [] }) {
  const navigate = useNavigate();

  return (
    <Box
      className="education-shell-wrapper"
      sx={{
        fontFamily: "'DM Sans', sans-serif",
        bgcolor: "#FFFFFF",
        minHeight: "100vh",
        
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Fraunces:ital,wght@0,300;0,700;1,300;1,700&display=swap');
      `}</style>

      {/* Lightweight header: back button + breadcrumb only — no tabs, no dropdowns */}
      {breadcrumbs.length > 0 && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 50,
            bgcolor: "rgba(255,255,255,0.98)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #E5E7EB",
            px: { xs: 2, md: 6 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton size="small" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Breadcrumbs
            separator="›"
            sx={{
              "& .MuiBreadcrumbs-separator": { color: "#D1D5DB", mx: 0.5 },
              fontSize: { xs: "0.78rem", md: "0.85rem" },
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              if (isLast || !crumb.path) {
                return (
                  <Typography
                    key={crumb.label}
                    sx={{ fontSize: "inherit", fontWeight: isLast ? 600 : 500, color: isLast ? "#1F2937" : "#6B7280" }}
                  >
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Typography
                  key={crumb.label}
                  onClick={() => navigate(crumb.path)}
                  sx={{ fontSize: "inherit", fontWeight: 500, color: "#1A5EA8", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  {crumb.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {/* Page content */}
      <Box>{children}</Box>

      {/* Footer: Sources & References — unchanged */}
      <Box sx={{ borderTop: "1px solid #E5E7EB", bgcolor: "#F9FAFB", px: { xs: 2, md: 6 }, py: 4, mt: 6 }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", mb: 1.5 }}>
          Sources &amp; References
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#6B7280", mb: 2, maxWidth: 640 }}>
          Information on this page draws on established medical resources. It is educational in nature and is
          not a substitute for advice from your doctor or care team.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {DEFAULT_SOURCES.map((source) => (
            <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1A5EA8", textDecoration: "none", padding: "6px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#FFFFFF" }}>
              {source.name}
            </a>
          ))}
        </Box>
      </Box>
    </Box>
  );
}